import React from "react";
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button as MuiButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  useTheme
} from "@mui/material";
import { Warning, CheckCircle, ErrorOutline } from "@mui/icons-material";
import { profJimBrandColors } from "../../theme/variants";

interface ErrorHandlingModalProps {
  error?: {
    message: string;
    stack?: string;
  };
  onClose?: () => void;
  onReportIssue?: () => Promise<void>;
}

export const ErrorHandlingModal: React.FC<ErrorHandlingModalProps> = ({
  error,
  onClose,
  onReportIssue
}) => {
  const theme = useTheme();
  const errorMessage = error?.message || "An unknown error occurred";
  const errorStack = error?.stack || "No stack trace available";
  const [isReporting, setIsReporting] = React.useState(false);

  const troubleshootingSteps = [
    "Move to the next section",
    "Clear your browser cache",
    "Try another browser",
    "Check your internet connection",
  ];

  const handleReportIssue = async () => {
    if (!onReportIssue) {
      console.log("Report issue handler not provided");
      return;
    }
    
    try {
      setIsReporting(true);
      await onReportIssue();
      alert("Thank you for reporting this issue. Our team has been notified.");
    } catch (error) {
      console.error("Error reporting issue:", error);
      alert("Failed to send error report. Please try again later.");
    } finally {
      setIsReporting(false);
      // Close if onClose is provided
      if (onClose) onClose();
    }
  };

  return (
    <Card 
      elevation={3}
      sx={{ 
        maxWidth: 550, 
        mx: 'auto', 
        mt: 4, 
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ 
        bgcolor: profJimBrandColors.purple, 
        color: 'white', 
        p: 2 
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'bold', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: 'white'
          }}
        >
          <ErrorOutline />
          Error Encountered
        </Typography>
        <Typography variant="body1" sx={{ color: 'white', mt: 1 }}>
          We're sorry, something went wrong while playing your interactive.
        </Typography>
      </Box>
      
      <CardContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Error details:
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 120,
              overflow: 'auto'
            }}
          >
            <Typography color="error.dark" fontFamily="monospace" fontSize="0.875rem">
              {errorMessage}
            </Typography>
            {errorStack && (
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography color="text.secondary" fontSize="0.75rem" fontFamily="monospace">
                  {errorStack.split("\n")[0]}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
        
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            How to continue watching:
          </Typography>
          <List 
            dense 
            disablePadding
            sx={{ 
              '& .MuiListItem-root': {
                py: 0.25
              }
            }}
          >
            {troubleshootingSteps.map((step, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle fontSize="small" sx={{ color: profJimBrandColors.purple }} />
                </ListItemIcon>
                <ListItemText 
                  primary={step} 
                  primaryTypographyProps={{ 
                    fontSize: '0.9rem'
                  }} 
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </CardContent>
      
      <Divider />
      
      <CardActions sx={{ p: 2, bgcolor: 'grey.50', justifyContent: 'center' }}>
        <MuiButton
          variant="contained"
          style={{ backgroundColor: profJimBrandColors.purple }}
          startIcon={<Warning />}
          onClick={handleReportIssue}
          disabled={isReporting}
          sx={{ 
            borderRadius: 6,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'medium',
            '&:hover': {
              backgroundColor: `${profJimBrandColors.purple}dd`
            }
          }}
        >
          {isReporting ? "Sending report..." : "Report this issue"}
        </MuiButton>
      </CardActions>
    </Card>
  );
};

export default ErrorHandlingModal;
